import { isInternalEventLink } from "@/lib/utils/linkUtils";

const trailingInternalPathPunctuationRegex = /[.,!?;:。、！？]+$/;
const mentionLinkTitleRegex =
  /(?:^|\s)(["'])mention-(?:user|studygroup)-[^"']+\1(?:\s|$)/;

const isMentionMarkdownDestination = (destination: string): boolean =>
  destination.trim().startsWith("mention:") ||
  mentionLinkTitleRegex.test(destination);

const extractMarkdownLinkDestinationUrl = (destination: string): string => {
  const trimmedDestination = destination.trim();
  const angleBracketMatch = /^<([^>]+)>/.exec(trimmedDestination);

  if (angleBracketMatch?.[1]) {
    return angleBracketMatch[1];
  }

  return trimmedDestination.split(/\s+/)[0] ?? "";
};

/**
 * セキュアなURL検証
 *
 * @param url 検証するURL文字列
 * @param options 検証オプション
 * @returns URLが安全かどうか
 */
export function isSafeUrl(
  url: string,
  options: {
    /**
     * file-guid: プロトコルを許可するかどうか
     * @default false
     */
    allowFileGuid?: boolean;
  } = {},
): boolean {
  if (!url || typeof url !== "string") return false;

  const allowedProtocols = ["http:", "https:"];
  if (options.allowFileGuid) {
    allowedProtocols.push("file-guid:");
  }

  try {
    // Use a dummy base to correctly parse relative, absolute, and protocol-relative URLs.
    const parsed = new URL(url, "http://dummy.base");
    return allowedProtocols.includes(parsed.protocol);
  } catch {
    // An invalid URL is not a safe URL.
    return false;
  }
}

/**
 * Check if a URL should show a preview
 */
export function shouldShowPreview(url: string): boolean {
  // まず安全性をチェック
  if (!isSafeUrl(url)) {
    return false;
  }

  // 内部イベントリンクは常にプレビューを表示
  if (isInternalEventLink(url)) {
    return true;
  }

  try {
    const urlObj = new URL(url);

    // Skip certain domains or file types that don't need previews
    const skipDomains = ["localhost", "127.0.0.1"];
    const skipExtensions = [
      ".pdf",
      ".jpg",
      ".jpeg",
      ".png",
      ".gif",
      ".svg",
      ".mp4",
      ".mp3",
    ];

    // Check if domain should be skipped
    if (skipDomains.some((domain) => urlObj.hostname.includes(domain))) {
      return false;
    }

    // Check if extension should be skipped
    if (
      skipExtensions.some((ext) => urlObj.pathname.toLowerCase().endsWith(ext))
    ) {
      return false;
    }

    return true;
  } catch {
    return false;
  }
}

/**
 * Extract URLs from markdown content
 */
export function extractLinksFromMarkdown(content: string): string[] {
  const links: string[] = [];

  // Match markdown links [text](url)
  const markdownLinkRegex = /\[([^\]]*)\]\(([^)]+)\)/g;
  let match: RegExpExecArray | null;
  match = markdownLinkRegex.exec(content);
  while (match !== null) {
    const destination = match[2];
    const url = extractMarkdownLinkDestinationUrl(destination);
    // mention:で始まるリンク、表示用メンションリンク、危険なURLを除外
    if (!isMentionMarkdownDestination(destination) && isSafeUrl(url)) {
      links.push(url);
    }
    match = markdownLinkRegex.exec(content);
  }

  // Match plain URLs (http/https only)
  const urlRegex = /https?:\/\/[^\s<>"{}|\\^`[\]]+/g;
  const urlMatches = content.match(urlRegex);
  if (urlMatches) {
    // 安全なURLのみ追加
    const safeUrls = urlMatches.filter((url) => isSafeUrl(url));
    links.push(...safeUrls);
  }

  // Match internal paths such as /study-group/1/post/2, /all-study-groups/1 and /chat-room/1
  const internalPathRegex =
    /(^|[\s([>])((?:\/study-group\/|\/all-study-groups\/|\/chat-room\/)[^\s<>"{}|\\^`[\]()]+)/g;
  const internalPathMatches = [...content.matchAll(internalPathRegex)];
  if (internalPathMatches.length > 0) {
    const safeInternalPaths = internalPathMatches
      .map((match) => {
        const prefix = match[1];
        const isMarkdownLinkDestination =
          prefix === "(" && content[match.index - 1] === "]";

        if (isMarkdownLinkDestination) {
          return null;
        }

        return match[2]?.replace(trailingInternalPathPunctuationRegex, "");
      })
      .filter((url): url is string => {
        return typeof url === "string" && isSafeUrl(url);
      });
    links.push(...safeInternalPaths);
  }

  // Remove duplicates and return
  return [...new Set(links)];
}
